import React, { useState } from 'react';
import { Input, Label } from 'reactstrap';

import ErrorResponse from '~components/ErrorResponse';
import TooltipIcon from '~components/TooltipIcon';
import { faInfoCircle } from '~components/icon';

import { useGetSettingsQuery } from '~lookups/actions/settings';
import {
  useCreateCompanySettingMutation,
  useGetCompanySettingsQuery,
  useUpdateCompanySettingMutation,
} from '~main/actions/companySettings';

interface ComponentProps {
  isActive: boolean;
}

const FEATURE_TAG = 'sah_statements';
const SETTING_KEY = 'statement_language_mode';
const DEFAULT_VALUE = 'multilingual';

const TOOLTIP_TEXT =
  'Multilingual: statement is rendered in the client’s chosen language only (current behaviour). ' +
  'Bilingual: translated language shown first, with English below each label.';

const StatementSettings: React.FC<ComponentProps> = ({ isActive }) => {
  const { data: definitions = [] } = useGetSettingsQuery({ feature_tag: FEATURE_TAG }, { skip: !isActive });
  const { data: settings = [] } = useGetCompanySettingsQuery(undefined, { skip: !isActive });
  const [createSetting] = useCreateCompanySettingMutation();
  const [updateSetting] = useUpdateCompanySettingMutation();

  const [localValue, setLocalValue] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const definition = definitions.find((def) => def.key === SETTING_KEY);
  const stored = definition ? settings.find((ss) => ss.setting.id === definition.id) : undefined;
  const savedValue = stored?.value ?? DEFAULT_VALUE;
  const value = localValue ?? savedValue;
  const isNew = !stored;

  const handleChange = async (newValue: string) => {
    if (!definition) return;
    setLocalValue(newValue);
    setSaving(true);
    setMutationError(null);
    try {
      if (isNew) {
        await createSetting({ lookup_setting_id: definition.id, value: newValue }).unwrap();
      } else {
        await updateSetting({ id: stored.id, value: newValue }).unwrap();
      }
    } catch (err) {
      console.error('Failed to save statement language mode setting:', err);
      setLocalValue(null);
      setMutationError('Failed to save setting. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="company-settings-form">
      <div className="setting-row">
        <div className="d-flex align-items-center gap-2">
          <Label>Statement Language Mode</Label>
          <TooltipIcon id="statement-language-mode" icon={faInfoCircle} tooltip={TOOLTIP_TEXT} />
          {saving && <span className="spinner-border spinner-border-sm" aria-label="Saving setting" />}
        </div>
        <div className="d-flex flex-column gap-2" data-cy="sah-statement-language-mode-options">
          {['multilingual', 'bilingual'].map((option) => (
            <Label key={option} check htmlFor={`statement-language-mode-${option}`} className="d-flex align-items-center gap-2">
              <Input
                id={`statement-language-mode-${option}`}
                type="radio"
                name="statement_language_mode"
                value={option}
                checked={value === option}
                disabled={saving}
                onChange={() => handleChange(option)}
                data-cy={`sah-statement-language-mode-${option}`}
              />
              {option === 'multilingual'
                ? 'Multilingual — statement rendered in the client’s chosen language only (current behaviour, default)'
                : 'Bilingual — translated language first, English shown below each label'}
            </Label>
          ))}
        </div>
        <ErrorResponse message={mutationError} />
      </div>
    </div>
  );
};

export default StatementSettings;
